"use client";

/**
 * SINGLE SOURCE OF TRUTH — dispatch rendererów CMS per templatu szablonu CMS.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * # ARCHITEKTURA PER-TEMPLATE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Po cutover 2026-05-27 (III) — konfiguracja jest **per template CMS**
 * (analog do legacy Imperial: `template/{symbol}/layout/*.imp` +
 * `template/{symbol}/container/*.imp` — view files per template, kontrolery
 * wspólne). Każdy template ma własny katalog z dispatch'em:
 *
 *   components/cms/registry/templates/{symbol}/
 *     ├─ windowVariants.tsx     — dispatch okien (w_*) dla tego template'u
 *     ├─ catalogVariants.tsx    — dispatch katalogów (c_*) dla tego template'u
 *     └─ allowedAreas.ts        — dozwolone obszary per typ okna (server-safe)
 *
 * **Komponenty wspólne** (`WMenu`, `CDescriptivePage`, `CDefault`, `WEmpty` itp.)
 * pozostają w `components/{windows,catalogs,cms/registry}/` i są używane przez
 * wszystkie templates — różni się tylko dispatch lambda + CSS (jeśli komponent
 * używa motyw-zależnych zmiennych `--color-*`).
 *
 * **Templates registry** w tym pliku — `TEMPLATES` map. Dodanie nowego template'u:
 *   1. Stwórz katalog `templates/{symbol}/` z 3 plikami (wzorzec: `default/`)
 *   2. Dodaj import + entry w mapie `TEMPLATES` poniżej
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * # DISPATCH RESOLUTION
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   resolveWindowVariant(componentSymbol, area, template):
 *     templates[template].areaOverrides[componentSymbol]?.[area]
 *     ?? templates[template].defaults[componentSymbol]
 *     ?? templates['default'].areaOverrides[componentSymbol]?.[area]    // fallback do default template
 *     ?? templates['default'].defaults[componentSymbol]
 *     ?? null                                                           // AlignmentChecker dev banner
 *
 *   resolveCatalogVariant(componentSymbol, template, area='catalogContent'):
 *     analogicznie
 *
 * **Fallback do `default`:** jeśli aktywny template nie ma entry dla danego
 * `componentSymbol`, używamy `default`. To pozwala templates'om opisywać
 * TYLKO RÓŻNICE (np. `dark_brand` może override'ować tylko `w_menu`, reszta
 * z `default`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * # BACKEND NIE WIE O WARIANTACH
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Po cutover 2026-05-27 tabele DB warstwy "kontenerów" zostały USUNIĘTE.
 * Backend zwraca tylko `(componentSymbol, area, title, content, background)`
 * + `cmsConfig.template` (symbol aktywnego szablonu). React-CMS dispatches
 * wizualnego wrappera frontend-only przez `resolveXxxVariant(...,template)`.
 *
 * Wyznacznik: jeśli zmiana ma być widoczna w UI bez restartu PHP — wpisuj
 * ją do pliku w `templates/{template}/`, nie do DB.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * # JAK DODAĆ NOWY TYP OKNA (w_xxx) — w istniejącym templatcie
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   1. Backend: `getApiContent()` w `component/w_xxx/w_xxx.php`.
 *   2. React (komponent wspólny): `components/windows/WXxx/WXxx.tsx`.
 *   3. Dispatch: wpis w `templates/{template}/windowVariants.tsx -> defaults`:
 *        w_xxx: ({ content, background }) => (
 *          <WEmpty background={background}>
 *            <WXxx content={content as WXxxContent} />
 *          </WEmpty>
 *        )
 *      (lub `WDefault` z tytułem gdy okno ma header instancji).
 *   4. (Opcjonalnie) `templates/{template}/allowedAreas.ts -> windowAllowedAreas`
 *      jeśli okno ma constraint area-key.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * # JAK DODAĆ NOWY TEMPLATE CMS (np. `dark_brand`)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   1. Stwórz katalog `templates/dark_brand/` z 3 plikami:
 *      - `windowVariants.tsx` — dispatch okien (możesz skopiować z `default/`
 *        i zmodyfikować TYLKO te wpisy które różnią się; reszta fallback'uje
 *        do `default` automatycznie przez resolve helper).
 *      - `catalogVariants.tsx` — analogicznie.
 *      - `allowedAreas.ts` — analogicznie.
 *   2. Dodaj import + entry w mapie `TEMPLATES` poniżej.
 *   3. Po deployu backend musi mieć symbol `dark_brand` w `imp_cms_template`
 *      + aktywować dla domeny przez `imp_cms_domain_template`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * # KONWENCJA NAZEWNICZA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   Okna:    `W{Type}.tsx`        — default renderer (komponent)
 *            `W{Type}{Variant}.tsx` — variant (visual override per area)
 *   Katalogi: `C{Type}.tsx` / `C{Type}{Variant}.tsx`
 *   Kontenery: `WDefault`/`WEmpty` (okno) + `CDefault`/`CEmpty` (katalog)
 *
 *   Variant suffix opisuje WIZUALNY charakter (`GreyBack`/`Card`/`Banner`) —
 *   NIE area-key. Pozwala reuse'ować ten sam variant w wielu strefach.
 */

import type { AreaSymbol } from "@/lib/layouts/types";

import { windowVariants as defaultWindowVariants } from "./templates/default/windowVariants";
import { catalogVariants as defaultCatalogVariants } from "./templates/default/catalogVariants";

import type {
  WindowRenderer,
  CatalogRenderer,
  WindowVariants,
  CatalogVariants,
} from "./types";
import { DEFAULT_TEMPLATE_SYMBOL } from "./types";

/* ═══ Templates registry ══════════════════════════════════════════════════ */

/**
 * Mapa symbol templatu -> variants. Resolve fallback'uje do `default` gdy
 * aktywny template brak entry dla danego `componentSymbol`.
 *
 * Dodanie nowego template'u: import + entry. Aktualnie tylko `default` —
 * gdy `dark_brand` (lub inny) zechce override'ować dispatch, stwórz folder
 * `templates/dark_brand/` i dorzuć tutaj.
 */
const TEMPLATES: Record<
  string,
  { windowVariants: WindowVariants; catalogVariants: CatalogVariants }
> = {
  [DEFAULT_TEMPLATE_SYMBOL]: {
    windowVariants: defaultWindowVariants,
    catalogVariants: defaultCatalogVariants,
  },
};

/* ═══ Resolution helpers ══════════════════════════════════════════════════ */

/**
 * Normalize template symbol — fallback do `'default'` gdy null/empty/unknown.
 *
 * Caller zwykle przekazuje `cmsConfig.template` (z PublicLayout server-side)
 * lub `useAdmin().activeTemplate` (z client component). Może być null gdy
 * domena nie ma ustawionego szablonu.
 */
function resolveTemplate(template: string | null | undefined): string {
  if (!template) return DEFAULT_TEMPLATE_SYMBOL;
  if (TEMPLATES[template]) return template;
  return DEFAULT_TEMPLATE_SYMBOL;
}

/**
 * Resolve renderera dla okna w danej strefie + templacie.
 *
 * Fallback chain:
 *   1. templates[template].areaOverrides[symbol]?.[area]
 *   2. templates[template].defaults[symbol]
 *   3. templates['default'].areaOverrides[symbol]?.[area]   ← fallback do default template
 *   4. templates['default'].defaults[symbol]
 *   5. null -> AlignmentChecker pokaże dev banner "brak renderera dla typu X"
 */
export function resolveWindowVariant(
  componentSymbol: string,
  area: AreaSymbol,
  template: string | null | undefined,
): WindowRenderer | null {
  const tpl = resolveTemplate(template);
  const tplVariants = TEMPLATES[tpl].windowVariants;
  const baseVariants = TEMPLATES[DEFAULT_TEMPLATE_SYMBOL].windowVariants;

  return (
    tplVariants.areaOverrides[componentSymbol]?.[area]
    ?? tplVariants.defaults[componentSymbol]
    ?? (tpl !== DEFAULT_TEMPLATE_SYMBOL
      ? (baseVariants.areaOverrides[componentSymbol]?.[area]
        ?? baseVariants.defaults[componentSymbol])
      : undefined)
    ?? null
  );
}

/**
 * Resolve renderera dla katalogu w danym templacie.
 *
 * @param area Domyślnie `catalogContent` (jedyna strefa katalogu).
 */
export function resolveCatalogVariant(
  componentSymbol: string,
  template: string | null | undefined,
  area: AreaSymbol = "catalogContent" as AreaSymbol,
): CatalogRenderer | null {
  const tpl = resolveTemplate(template);
  const tplVariants = TEMPLATES[tpl].catalogVariants;
  const baseVariants = TEMPLATES[DEFAULT_TEMPLATE_SYMBOL].catalogVariants;

  return (
    tplVariants.areaOverrides[componentSymbol]?.[area]
    ?? tplVariants.defaults[componentSymbol]
    ?? (tpl !== DEFAULT_TEMPLATE_SYMBOL
      ? (baseVariants.areaOverrides[componentSymbol]?.[area]
        ?? baseVariants.defaults[componentSymbol])
      : undefined)
    ?? null
  );
}

/* ═══ AlignmentChecker support ════════════════════════════════════════════ */

/* Note: `windowAllowedAreas` + `getWindowAllowedAreas()` żyją w osobnym pliku
   `./allowedAreas.ts` (BEZ "use client") bo są konsumowane też przez Server
   Components (Adapter `lib/windows/adapters.ts -> buildMeta` wywoływany przez
   `PublicLayout` server-side). Ten plik (variants.tsx) jest client-only przez
   dynamic import `WPrezentation` (w templates/{symbol}/windowVariants.tsx). */

/**
 * Union zarejestrowanych componentSymbol dla okien — z WSZYSTKICH templates.
 * AlignmentChecker używa do detekcji "okno ma backend API ale brak React
 * renderera w żadnym z templates'ów" (alarm gdy nawet `default` go nie ma).
 *
 * Per-template alignment (typ obecny w `default` ale brak w aktywnym
 * `dark_brand` z explicit override?) — nie checkowane w obecnej iteracji
 * (resolve robi auto-fallback do `default`).
 */
export const KNOWN_WINDOW_SYMBOLS: ReadonlyArray<string> = Array.from(
  new Set(
    Object.values(TEMPLATES).flatMap((tpl) =>
      Object.keys(tpl.windowVariants.defaults),
    ),
  ),
);

/**
 * Union zarejestrowanych componentSymbol dla katalogów — z WSZYSTKICH templates.
 */
export const KNOWN_CATALOG_SYMBOLS: ReadonlyArray<string> = Array.from(
  new Set(
    Object.values(TEMPLATES).flatMap((tpl) =>
      Object.keys(tpl.catalogVariants.defaults),
    ),
  ),
);
